import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useBoolean, useToast } from '@chakra-ui/react';
import type { NodeApi, TreeApi } from 'react-arborist';
import Swal from 'sweetalert2';

import type {
    MigrationScriptGroup,
    MigrationScriptGroupFolder,
    MigrationSystemAllScriptGoupAndDatabase,
    WebSocketRequest,
    WebSocketResponse
} from 'models/Migration';

import { isJson, isPing, isWebSocketResponse } from 'commons/helpers';
import { useLoader } from 'commons/hooks';
import { getDbDateTime } from 'commons/common';
import { getMigrationScriptContent, MigrationScriptResponse } from 'data/Migrate';


function NewMigrationViewModel() {
    const screenLoader = useLoader();
    const toast = useToast({ duration: 5000, isClosable: true });

    const virtualTerminalRef = useRef<HTMLDivElement>(null);
    const [files, setFiles] = useState<MigrationScriptResponse[]>([]);
    const [outputLogs, setOutputLogs] = useState<WebSocketResponse[]>([]);

    const [showSkippableModal, setShowSkippableModal] = useBoolean();
    const [error, setError] = useState({ fileName: '', message: '', indexOfTransaction: 0 });
    const skipResolverRef = useRef<(value: 'skip' | 'skip_all' | 'cancel') => void>();

    const wsRef = useRef<WebSocket>();
    const connectedRef = useRef<WebSocketRequest>();

    const [selectedFileContent, setSelectedFileContent] = useState('// กรุณาเลือกไฟล์จากรายการด้านซ้าย');

    const [scriptGroupDatabase, setScriptGroupDatabase] =
        useState<MigrationSystemAllScriptGoupAndDatabase[]>();

    const [selectedFile, setSelectedFile] = useState<MigrationScriptResponse | null>(null);
    const [selectedForRun, setSelectedForRun] = useState<string[]>([]);

    useEffect(() => {
        virtualTerminalRef.current?.scrollTo(0, virtualTerminalRef.current.scrollHeight);
    }, [outputLogs]);

    useEffect(() => {
        fetchScriptGroptOptions();
    }, []);

    const fetchScriptGroptOptions = async () => {
        try {
            const allScriptGoupAndDatabase = (
                await axios.get<MigrationSystemAllScriptGoupAndDatabase[]>(
                    '/v1/migration-scriptGroup-database'
                )
            ).data;
            setScriptGroupDatabase(allScriptGoupAndDatabase!);
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleClickConnect = useCallback(async (connection: WebSocketRequest) => {
        try {
            screenLoader.show();

            await connectToWebSocketAndDatabase(connection);

            const scripts = (
                await axios.get<MigrationScriptResponse[]>('/v1/get-all-migration-scriptGroup', {
                    params: {
                        company: connection.script_group
                    }
                })
            ).data;

            connectedRef.current = connection;
            setFiles(scripts);
        } catch (error) {
            if (error instanceof AxiosError) {
                Swal.fire(
                    'Error!',
                    'Cannot connect to this connection ' + (error as AxiosError).response?.data,
                    'error'
                );
            } else if (error instanceof Error) {
                Swal.fire('Error!', error.message, 'error');
            }
        } finally {
            screenLoader.hide();
        }
    }, []);

    const connectToWebSocketAndDatabase = useCallback((connection: WebSocketRequest) => {
        return new Promise<void>((resolve, reject) => {
            const url = window.location.href.includes('local')
                ? 'ws://localhost:8082/ws-v2'
                : 'wss://ag-management-api.sonarinno.com/ws-v2';

            if (connectedRef.current?.connection_id) {
                reject(new Error('Database already connected.'));

                return;
            }

            wsRef.current = new WebSocket(url);
            const ws = wsRef.current;

            // Listening open connection
            ws.addEventListener('open', _event => {
                // Send connect to database after connected to websocket
                ws.send(
                    JSON.stringify({
                        username: 'focusone',
                        client_action: 'connect',
                        value: '',
                        host: connection.host,
                        database: connection.database,
                        connection_id: '',
                        script_group: connection.script_group,
                        folder: '',
                        id: '',
                        index_of_transaction: 0,
                        file_url: ''
                    })
                );
            });

            // Listening the database is connected or not
            let onMessage: (this: WebSocket, event: MessageEvent<string | any>) => void;
            ws.addEventListener(
                'message',
                (onMessage = event => {
                    if (!isJson(event.data)) {
                        return;
                    }

                    const data = JSON.parse(event.data);
                    if (!isWebSocketResponse(data)) {
                        return;
                    }

                    if (
                        !data.connection_id ||
                        !data.detail.status ||
                        data.detail.status !== 'success'
                    ) {
                        ws.close(200, 'Database is not connected.');
                        delete wsRef.current;

                        // Return to caller but doesn't process the rest of the script below
                        reject();

                        return;
                    }

                    // Save connection id and connection string
                    connection.connection_id = data.connection_id;
                    connectedRef.current = connection;

                    // Show success toast
                    toast({ title: 'Connected to server success!', duration: 6000 });

                    // Return to caller but still processing the rest of the script below
                    resolve();

                    // Remove this listener
                    ws.removeEventListener('message', onMessage);
                })
            );

            // Listening ping from server
            ws.addEventListener('message', event => {
                console.info('Received ping?', event.data);

                if (!isJson(event.data)) {
                    return;
                }

                const data: WebSocketResponse = JSON.parse(event.data);

                // Close the connection when connect to database failed.
                if (data.status && data.status === 'error') {
                    toast({
                        status: 'error',
                        title: data.message ?? 'Database is not connected.',
                        duration: 6000
                    });

                    ws.close(1000, data.message);

                    delete wsRef.current;

                    reject();

                    return;
                }

                if (!isPing(data)) {
                    return;
                }

                const currentConnection = connectedRef.current ?? connection;
                ws.send(
                    JSON.stringify({
                        username: 'focusone',
                        client_action: 'pong',
                        script_group: currentConnection.script_group,
                        host: currentConnection.host,
                        database: currentConnection.database,
                        connection_id: currentConnection.connection_id
                    } as WebSocketRequest)
                );

                // Debugging pong
                console.info('pong', event.data);
            });
        });
    }, []);

    const handleClickExportRunningLog = useCallback(() => {
        const virtualTerminal = virtualTerminalRef.current;
        if (!virtualTerminal) {
            Swal.fire('Error!', 'Virtual terminal is not ready', 'error');

            return;
        }

        const outputLogsContainer = virtualTerminal.querySelector('#output-log-container');
        if (!outputLogsContainer) {
            Swal.fire('Error!', 'Output log is not ready', 'error');

            return;
        }

        const logs = outputLogsContainer.innerHTML
            .replace(/<style([\s\S]*?)<\/style>/gi, '')
            .replace(/<script([\s\S]*?)<\/script>/gi, '')
            .replace(/<\/div>/gi, '\n')
            .replace(/<\/li>/gi, '\n')
            .replace(/<li>/gi, '  *  ')
            .replace(/<\/ul>/gi, '\n')
            .replace(/<\/p>/gi, '\n')
            .replace(/<br\s*[\/]?>/gi, '\n')
            .replace(/<[^>]+>/gi, '');

        const blob = new Blob([logs], { type: 'octet/stream' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = `execute-logs-${getDbDateTime()}.txt`;
        a.click();

        URL.revokeObjectURL(url);
    }, []);

    const handleFileSelect = async (file: MigrationScriptResponse) => {
        if (selectedFile?.s3_guid === file.s3_guid) {
            return;
        }

        setSelectedFile(file);
        setSelectedFileContent(`-- Loading ${file.file_name}...`);

        try {
            const content = await getMigrationScriptContent(file.s3_file_path);
            setSelectedFileContent(content);
        } catch (error) {
            console.error("Failed to fetch file content:", error);
            setSelectedFileContent(`-- Error loading file: ${file.file_name}\n\n`);
        }
    };

    const handleClickRunSelectedFiles = useCallback(() => {
        const scriptsToRun = files.filter(file => selectedForRun.includes(file.s3_guid));

        if (scriptsToRun.length === 0) {
            Swal.fire('Info', 'กรุณาติ๊กเลือกไฟล์ที่ต้องการจะรันก่อน', 'info');
            return;
        }

        runScripts(scriptsToRun);
    }, [files, selectedForRun]);

    const handleClickRunAllFiles = useCallback(() => {
        if (!files || files.length === 0) {
            Swal.fire('Info', 'ไม่มีไฟล์ให้รัน', 'info');
            return;
        }
        runScripts(files);
    }, [files]);

    const runScripts = async (scriptsToRun: MigrationScriptResponse[]) => { //แก้ๆ
        const ws = wsRef.current;
        if (!ws) {
            Swal.fire('Error', 'ไม่สามารถเชื่อมต่อ Server ได้ กรุณาเชื่อมต่อใหม่อีกครั้ง', 'error');
            return;
        }

        const connection = connectedRef.current;
        if (!connection) {
            Swal.fire(
                'Error',
                'ไม่สามารถเชื่อมต่อกับฐานข้อมูล กรุณาเชื่อมต่อใหม่อีกครั้ง',
                'error'
            );
            return;
        }

        if (!scriptsToRun || scriptsToRun.length === 0) {
            Swal.fire('Info', 'ไม่มีสคริปต์ที่เลือกให้รัน', 'info');
            return;
        }

        const result = await Swal.fire({
            title: 'Warning',
            text: `ยืนยันที่จะรันสคริปต์จำนวน ${scriptsToRun.length} ไฟล์`,
            icon: 'warning',
            showCancelButton: true
        });

        if (!result.isConfirmed) {
            return;
        }

        let workingRequest: WebSocketRequest | undefined;
        let resolver: () => void;

        let onMessage: (this: WebSocket, event: MessageEvent<string>) => void;
        ws.addEventListener(
            'message',
            (onMessage = async (event: MessageEvent<string>) => {
                if (!isJson(event.data)) {
                    setOutputLogs(prevState => [
                        ...prevState,
                        {
                            script_group: workingRequest?.script_group ?? 'UNKNOWN-SCRIPT_GROUP',
                            connection_id: workingRequest?.connection_id ?? '',
                            folder: workingRequest?.folder ?? '',
                            id: workingRequest?.id ?? '',
                            detail: {
                                status: '',
                                message: event.data,
                                index_of_transaction: 0,
                                elapsed_time: ''
                            }
                        }
                    ]);

                    return;
                }

                const response = JSON.parse(event.data);
                if (!isWebSocketResponse(response)) {
                    return;
                }

                setOutputLogs(prevState => [...prevState, response]);

                if (response.detail.status !== 'success') {
                    const skippingType = await new Promise<'skip' | 'skip_all' | 'cancel'>(
                        resolve => {
                            skipResolverRef.current = resolve;

                            setShowSkippableModal.on();
                            setError({
                                fileName: workingRequest?.id ?? '',
                                message: response.detail.message,
                                indexOfTransaction: response.detail.index_of_transaction
                            });
                        }
                    );

                    ws.send(
                        JSON.stringify({
                            username: 'focusone',
                            connection_id: connection.connection_id,
                            client_action: skippingType,
                            value: '',
                            host: connection.host,
                            database: connection.database,
                            script_group: connection.script_group,
                            folder: response.folder,
                            id: response.id,
                            index_of_transaction: response.detail.index_of_transaction,
                            file_url: response.file_path
                        } as WebSocketRequest)
                    );
                } else {
                    resolver();
                }
            })
        );

        for (const file of scriptsToRun) {
            await new Promise<void>(resolve => {
                resolver = resolve;
                ws.send(
                    JSON.stringify(
                        (workingRequest = {
                            username: 'focusone',
                            connection_id: connection.connection_id,
                            client_action: 'exec',
                            value: '',
                            host: connection.host,
                            database: connection.database,
                            script_group: connection.script_group,
                            folder: file.group,
                            id: file.file_name,
                            index_of_transaction: 0,
                            file_url: file.s3_file_path
                        })
                    )
                );
            });
        }

        ws.removeEventListener('message', onMessage);
    };

    const updateSelectedForRun = (newSelection: string[]) => {
        setSelectedForRun(newSelection);
    };


    return {
        files,
        outputLogs,
        showSkippableModal,
        error,
        selectedFile,
        skipResolverRef,
        virtualTerminalRef,
        selectedFileContent,
        selectedForRun,

        handleFileSelect,
        handleClickConnect,
        updateSelectedForRun,
        setSelectedForRun,
        handleClickExportRunningLog,
        handleClickRunSelectedFiles,
        handleClickRunAllFiles,
        setShowSkippableModal,
        scriptGroupDatabase
    };
}

export default NewMigrationViewModel;
